import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUniversity } from "react-icons/fa";

import "../common/Util.css";
import "./paymentList.css";
import "./paymenthistoryList.css";
import { ReactComponent as SearchIc } from "../../assets/svg/search.svg";
import { ReactComponent as ImportIc } from "../../assets/svg/import_ic.svg";
import { ReactComponent as AddIc } from "../../assets/svg/add_icon.svg";
import { ReactComponent as FilterIc } from "../../assets/svg/his_status_ic.svg";
import { ReactComponent as CalIc } from "../../assets/svg/CalenderIc.svg";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { ReactComponent as EditIc } from "../../assets/svg/edit_ic.svg";
import { ReactComponent as DeleteIc } from "../../assets/svg/delete_ic.svg";
import { ReactComponent as CloseIc } from "../../assets/svg/close-modal.svg";
import profileIc from "../../assets/png/profile-ic.png";
import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import { Dropdown, Offcanvas } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { getPaymentsAPI, getPaymentHistoryListAPI } from "./payment-api";
import DateRangePicker from "../common/dateRange";
import { format } from "date-fns";
import PaymentReconciliation from "./paymentReconciliation";
import ProfileImageModal from "../common/common-preview";

function PaymentHistoryListingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [vendorCount, setVendorCount] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showViewDrawer, setShowViewDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "History",
  );
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState("");

  // Pagination and API data states
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [selectedSort, setSelectedSort] = useState({
    value: "created_at",
    label: "Date created",
    dir: "desc",
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedVendor, setSelectedVendor] = useState({
    value: "",
    label: "All",
  });
  const [vendorList, setVendorList] = useState([]);
  const [timePeriod, setTimePeriod] = useState("AllTime");

  // Reconciliation count states
  const [reconTotal, setReconTotal] = useState(0);
  const [reconMatch, setReconMatch] = useState(0);
  const [reconMismatch, setReconMismatch] = useState(0);

  useEffect(() => {
    const fetchVendorCount = async () => {
      try {
        const response = await getPaymentsAPI({ limit: 1, skip: 0 });
        if (response.success) {
          setVendorCount(response.total_rec || 0);
        }
      } catch (error) {
        console.error("Error fetching vendor count:", error);
      }
    };
    fetchVendorCount();

    const fetchVendorsForFilter = async () => {
      try {
        const response = await getPaymentsAPI({ limit: 100, skip: 0 });
        if (response.success) {
          const sortedVendors = (response.payments || []).sort((a, b) => {
            const nameA = (a.name || a.vendor || "").toLowerCase();
            const nameB = (b.name || b.vendor || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setVendorList(sortedVendors);
        }
      } catch (error) {
        console.error("Error fetching vendors for filter:", error);
      }
    };
    fetchVendorsForFilter();
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: itemsPerPage,
        skip: itemOffset,
        search: search,
        sort_field: selectedSort.value,
        sort_dir: selectedSort.dir,
        from_date: fromDate || null,
        to_date: toDate || null,
        vendor_id: selectedVendor.value || null,
      };

      if (timePeriod === "LastMonth") {
        const today = new Date();
        const firstDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const lastDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0,
        );

        const formatDate = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };
        params.from_date = formatDate(firstDayOfLastMonth);
        params.to_date = formatDate(lastDayOfLastMonth);
      }

      const response = await getPaymentHistoryListAPI(params);
      if (response.success) {
        setHistoryList(response.payments || []);
        setTotalCount(response.total_rec || 0);
        setPageCount(response.total_pgs || 0);
      } else {
        setHistoryList([]);
        setTotalCount(0);
        setPageCount(0);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      setHistoryList([]);
      setTotalCount(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    itemsPerPage,
    itemOffset,
    search,
    selectedSort,
    fromDate,
    toDate,
    selectedVendor,
    timePeriod,
  ]);

  useEffect(() => {
    if (activeTab === "History") {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const handleDateChange = ({ start, end }) => {
    const formattedStart = start ? format(start, "yyyy-MM-dd") : "";
    const formattedEnd = end ? format(end, "yyyy-MM-dd") : "";
    setFromDate(formattedStart);
    setToDate(formattedEnd);
    setTimePeriod(start && end ? "Custom" : "AllTime");
    setItemOffset(0);
    setCurrentPage(0);
  };

  const handlePageClick = (event) => {
    const newOffset = event.selected * itemsPerPage;
    setItemOffset(newOffset);
    setCurrentPage(event.selected);
  };

  const filteredHistoryList = historyList.filter((row) => {
    if (selectedType === "All") return true;
    return row.type?.toLowerCase() === selectedType.toLowerCase();
  });

  return (
    <div className="d-flex" style={{ width: "100%", overflowX: "hidden" }}>
      <ProfileImageModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        imageUrl={previewFileUrl}
      />
      <Sidebar currentfile="Payment" />
      <div className="rounded-4 flex-grow-1" style={{ minWidth: 0 }}>
        <Navbar pageTitle="Payments" />
        <div className="bg-half-white p-2">
          <div className="bg-white rounded-4 p-3">
            {/* First Row: Tabs & Main Actions */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="tab-container">
                <div
                  className="tab-item inactive"
                  onClick={() => navigate("/payment")}
                >
                  Vendor
                </div>
                <div
                  className={`tab-item ${activeTab === "History" ? "active" : "inactive"}`}
                  onClick={() => setActiveTab("History")}
                >
                  History {activeTab === "History" && `(${totalCount})`}
                </div>
                <div
                  className={`tab-item ${activeTab === "Reconciliation" ? "active" : "inactive"}`}
                  onClick={() => setActiveTab("Reconciliation")}
                >
                  Reconciliation{" "}
                  {activeTab === "Reconciliation" && `(${reconTotal})`}
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className="search-wrapper d-flex align-items-center gap-2 px-3 py-2">
                  <SearchIc />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="border-0 outline-0 rounded-3 fs-14 fw-500 textcolor"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setItemOffset(0);
                      setCurrentPage(0);
                    }}
                  />
                </div>
                <div
                  className="cursor-pointer my-box d-flex align-items-center justify-content-center gap-2 px-3"
                  onClick={() => navigate("/payment/create")}
                >
                  <AddIc style={{ width: "16px", height: "16px" }} />
                  <div className="fs-12 fw-600 text-white">New payment</div>
                </div>
                {activeTab === "History" && (
                  <div className="cursor-pointer my-import-btn d-flex align-items-center justify-content-center gap-2 px-3">
                    <ImportIc style={{ width: "16px", height: "16px" }} />
                    <div className="fs-12 fw-600 textPurple">Import</div>
                  </div>
                )}
              </div>
            </div>

            {/* Second Row: Filters */}
            <div className="d-flex align-items-center justify-content-between mt-4 filter-row-container">
              <div className="d-flex align-items-center gap-2">
                {/* Sort By Dropdown */}
                <Dropdown>
                  <Dropdown.Toggle
                    as="div"
                    className="filter-dropdown-item sort-by-filter cursor-pointer"
                  >
                    <span className="fs-12 textGrey">Sort by : </span>
                    <span className="fs-12 fw-600 textPurple">
                      {selectedSort.label}
                    </span>
                    <DownArrow className="ms-2 textPurple" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedSort({
                          value: "created_at",
                          label: "Date created",
                          dir: "desc",
                        });
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      Date created
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedSort({
                          value: "paying_amt",
                          label: "Amount: High to Low",
                          dir: "desc",
                        });
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      Amount: High to Low
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedSort({
                          value: "paying_amt",
                          label: "Amount: Low to High",
                          dir: "asc",
                        });
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      Amount: Low to High
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedSort({
                          value: "vendor_name",
                          label: "Vendor Name",
                          dir: "asc",
                        });
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      Vendor Name
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                {/* Date Range Picker */}
                <div
                  className="d-flex align-items-center px-1"
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #EBEBEB",
                    height: 34,
                    backgroundColor: "white",
                    width: "185px",
                    flexShrink: 0,
                  }}
                >
                  <span
                    className="fs-12 fw-400 textGrey me-1"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Date Range :
                  </span>
                  <span
                    className="dropdown-text1 textPurple"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <DateRangePicker onChange={handleDateChange} />
                  </span>
                </div>

                {/* Status / Type Dropdown */}
                <Dropdown>
                  <Dropdown.Toggle
                    as="div"
                    className="filter-dropdown-item status-all-filter cursor-pointer d-flex align-items-center justify-content-between w-100"
                  >
                    <div className="d-flex align-items-center min-width-0 flex-grow-1">
                      <span className="fs-12 textGrey flex-shrink-0">
                        Status :&nbsp;
                      </span>
                      <span
                        className="fs-12 fw-600 textPurple text-truncate d-inline-block"
                        style={{ maxWidth: "65px", verticalAlign: "middle" }}
                        title={selectedType}
                      >
                        {selectedType}
                      </span>
                    </div>
                    <DownArrow className="ms-2 textPurple flex-shrink-0" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {["All", "Cash", "UPI", "Bank Transfer", "Cheque"].map(
                      (t) => (
                        <Dropdown.Item
                          key={t}
                          onClick={() => {
                            setSelectedType(t);
                            setItemOffset(0);
                            setCurrentPage(0);
                          }}
                        >
                          {t}
                        </Dropdown.Item>
                      ),
                    )}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Vendor Dropdown */}
                <Dropdown>
                  <Dropdown.Toggle
                    as="div"
                    className="filter-dropdown-item vendor-all-filter cursor-pointer d-flex align-items-center justify-content-between w-100"
                  >
                    <div className="d-flex align-items-center min-width-0 flex-grow-1">
                      <span className="fs-12 textGrey flex-shrink-0">
                        Vendor :&nbsp;
                      </span>
                      <span
                        className="fs-12 fw-600 textPurple text-truncate d-inline-block"
                        style={{ maxWidth: "65px", verticalAlign: "middle" }}
                        title={selectedVendor.label}
                      >
                        {selectedVendor.label}
                      </span>
                    </div>
                    <DownArrow className="ms-2 textPurple flex-shrink-0" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedVendor({ value: "", label: "All" });
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      All
                    </Dropdown.Item>
                    {vendorList.map((v) => (
                      <Dropdown.Item
                        key={v.vendor_id || v.id}
                        onClick={() => {
                          setSelectedVendor({
                            value: v.vendor_id || v.id,
                            label: v.name || v.vendor,
                          });
                          setItemOffset(0);
                          setCurrentPage(0);
                        }}
                      >
                        {v.name || v.vendor}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Show (Time Period) Dropdown */}
                <Dropdown>
                  <Dropdown.Toggle
                    as="div"
                    className="filter-dropdown-item show-last-month-filter cursor-pointer"
                  >
                    <span className="fs-12 textGrey">Show : </span>
                    <span className="fs-12 fw-600 textPurple">
                      {timePeriod === "AllTime"
                        ? "All Time"
                        : timePeriod === "LastMonth"
                          ? "Last Month"
                          : "Custom"}
                    </span>
                    <DownArrow className="ms-2 textPurple" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => {
                        setTimePeriod("AllTime");
                        setFromDate("");
                        setToDate("");
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      All Time
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setTimePeriod("LastMonth");
                        setItemOffset(0);
                        setCurrentPage(0);
                      }}
                    >
                      Last Month
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {activeTab === "Reconciliation" && (
                <div className="d-flex align-items-center gap-2">
                  <div className="reconciliation-summary-box total">
                    <span className="fs-12 label">Total</span>
                    <span className="fs-12 value">{reconTotal}</span>
                  </div>
                  <div className="reconciliation-summary-box match">
                    <span className="fs-12 label">Match</span>
                    <span className="fs-12 value">{reconMatch}</span>
                  </div>
                  <div className="reconciliation-summary-box mismatch">
                    <span className="fs-12 label">Mismatch</span>
                    <span className="fs-12 value">{reconMismatch}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table Section */}
          {activeTab === "History" ? (
            <div className="bg-white rounded-4 mt-3 w-100 p-3" style={{ overflowX: "auto" }}>
              <div style={{ minWidth: "1200px" }}>
                {/* Table Header */}
              <div
                className="d-flex w-100 align-items-center px-2 py-2"
                style={{
                  backgroundColor: "#F2F2F2",
                  borderRadius: "8px 8px 0 0",
                  whiteSpace: "nowrap",
                }}
              >
                <div
                  className="fs-13 fw-700 textcolor px-3"
                  style={{ flex: 2, minWidth: 0 }}
                >
                  Vendor
                </div>
                <div
                  className="fs-13 fw-700 textcolor px-3"
                  style={{ flex: 1.5, minWidth: 0 }}
                >
                  Invoice No.
                </div>
                <div
                  className="fs-13 fw-700 textcolor text-end px-3"
                  style={{ flex: 1.5, minWidth: 0 }}
                >
                  Total Amount
                </div>
                <div
                  className="fs-13 fw-700 textcolor text-end px-3"
                  style={{ flex: 1.5, minWidth: 0 }}
                >
                  Paying Amount
                </div>
                <div
                  className="fs-13 fw-700 textcolor text-end px-3"
                  style={{ flex: 1.5, minWidth: 0 }}
                >
                  Paid Amount
                </div>
                <div
                  className="fs-13 fw-700 textcolor text-end px-3"
                  style={{ flex: 1.5, minWidth: 0 }}
                >
                  Pending Amount
                </div>
                <div
                  className="fs-13 fw-700 textcolor px-3"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  Type
                </div>
                <div
                  className="fs-13 fw-700 textcolor px-3"
                  style={{
                    flex: 3,
                    minWidth: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  Date & Time
                </div>
                <div
                  className="fs-13 fw-700 textcolor text-center px-3"
                  style={{ flex: 2, minWidth: 0 }}
                >
                  Attachments
                </div>
              </div>

              {/* Table Body */}
              <div
                style={{ width: "100%", minWidth: "1200px" }}
              >
                {loading ? (
                  <div className="text-center py-4 fs-13 textcolor">
                    Loading...
                  </div>
                ) : filteredHistoryList.length === 0 ? (
                  <div className="text-center py-4 fs-13 textcolor">
                    No payment history found
                  </div>
                ) : (
                  filteredHistoryList.map((row, index) => (
                    <div
                      key={row.id || index}
                      className="d-flex w-100 align-items-center px-2 py-2 border-bottom"
                      style={{
                        backgroundColor:
                          hoveredIndex === index ? "#FAFAFA" : "#FFFFFF",
                        cursor: "pointer",
                        height: "40px",
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => {
                        setSelectedRow(row);
                        setShowViewDrawer(true);
                      }}
                    >
                      <div
                        className="d-flex align-items-center px-3"
                        style={{ flex: 2, minWidth: 0 }}
                        title={row.vendor || "-"}
                      >
                        <img
                          src={row.profile ? row.profile : profileIc}
                          alt="vendor"
                          className="rounded-circle me-2"
                          style={{ width: "24px", height: "24px" }}
                        />
                        <span className="fs-13 fw-500 textcolor">
                          {row.vendor || "-"}
                        </span>
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor px-3"
                        style={{ flex: 1.5, minWidth: 0 }}
                        title={row.invoiceNo || "-"}
                      >
                        {row.invoiceNo || "-"}
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor text-end px-3"
                        style={{ flex: 1.5, minWidth: 0 }}
                        title={row.totalAmt}
                      >
                        {row.totalAmt}
                      </div>
                      <div
                        className="fs-13 fw-600 text-success text-end px-3"
                        style={{ flex: 1.5, minWidth: 0 }}
                        title={row.payingAmt}
                      >
                        {row.payingAmt}
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor text-end px-3"
                        style={{ flex: 1.5, minWidth: 0 }}
                        title={row.paidAmt}
                      >
                        {row.paidAmt}
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor text-end px-3"
                        style={{ flex: 1.5, minWidth: 0 }}
                        title={row.pendingAmt}
                      >
                        {row.pendingAmt}
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor px-3"
                        style={{ flex: 1, minWidth: 0 }}
                        title={row.type || "-"}
                      >
                        {row.type || "-"}
                      </div>
                      <div
                        className="fs-13 fw-500 textcolor px-3"
                        style={{
                          flex: 3,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                        }}
                        title={row.dateTime || row.datetime || "-"}
                      >
                        {row.dateTime || row.datetime || "-"}
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center px-3"
                        style={{ flex: 2, minWidth: 0 }}
                      >
                        {row.attachment ? (
                          <span
                            className="attachment-link text-primary text-decoration-underline"
                            style={{
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer",
                            }}
                            title={row.attachment}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (row.attachment_url) {
                                setPreviewFileUrl(row.attachment_url);
                                setShowPreviewModal(true);
                              }
                            }}
                          >
                            {row.attachment}
                          </span>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination Footer */}
              <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3 px-2">
                <Dropdown>
                  <span className="fs-12 fw-600 textcolor me-2">
                    Records per page :
                  </span>
                  <Dropdown.Toggle
                    variant="light"
                    className="bg-white border textcolor rounded-3 px-2 py-1 fs-12 fw-600"
                    style={{ minWidth: "50px" }}
                  >
                    {itemsPerPage} <DownArrow style={{ width: "10px" }} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {[10, 25, 50].map((size) => (
                      <Dropdown.Item
                        key={size}
                        onClick={() => {
                          setItemsPerPage(size);
                          setItemOffset(0);
                          setCurrentPage(0);
                        }}
                      >
                        {size}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <ReactPaginate
                  previousLabel={
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center border"
                      style={{ width: "32px", height: "32px" }}
                    >
                      <LarIc />
                    </div>
                  }
                  nextLabel={
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center border"
                      style={{ width: "32px", height: "32px" }}
                    >
                      <RarIc />
                    </div>
                  }
                  breakLabel={"..."}
                  pageCount={pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={currentPage}
                  containerClassName={
                    "pagination d-flex gap-2 align-items-center list-unstyled mb-0"
                  }
                  activeClassName={"active"}
                  pageClassName={"page-item"}
                  pageLinkClassName={
                    "page-link rounded-circle border-0 fs-12 fw-600 textcolor d-flex align-items-center justify-content-center"
                  }
                  activeLinkClassName={"bg-primary1 text-white"}
                />
              </div>
            </div>
          </div>
          ) : (
            <PaymentReconciliation
              search={search}
              status={selectedType}
              fromDate={fromDate}
              toDate={toDate}
              timePeriod={timePeriod}
              vendorId={selectedVendor.value}
              onCountsChange={({ total, match, mismatch }) => {
                setReconTotal(total);
                setReconMatch(match);
                setReconMismatch(mismatch);
              }}
            />
          )}
        </div>
      </div>

      <Offcanvas
        show={showViewDrawer}
        onHide={() => setShowViewDrawer(false)}
        placement="end"
        className="payment-view-drawer"
      >
        <Offcanvas.Header className="drawer-header-custom border-0 p-4 pb-2">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center gap-3">
              <div
                className="close-drawer-btn"
                onClick={() => setShowViewDrawer(false)}
              >
                <CloseIc style={{ width: "20px", height: "20px" }} />
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="amount-icon-bg">
                  <i className="bi bi-wallet2 fs-4 text-primary"></i>
                </div>
                <div>
                  <h3 className="drawer-amount mb-0">
                    {selectedRow?.payingAmt || "-"}
                  </h3>
                  <p className="drawer-subtext mb-0 mt-1">
                    Created on{" "}
                    {selectedRow?.dateTime || selectedRow?.datetime || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-4 pt-2">
          <div className="drawer-summary-cards d-flex gap-3 mb-4 border-top pt-3">
            <div className="summary-card flex-grow-1">
              <span className="card-label">Total Amount</span>
              <span className="card-value">{selectedRow?.totalAmt || "-"}</span>
            </div>
            <div className="summary-card flex-grow-1">
              <span className="card-label">Paid Amount</span>
              <span className="card-value">{selectedRow?.paidAmt || "-"}</span>
            </div>
            <div className="summary-card flex-grow-1">
              <span className="card-label">Pending Amount</span>
              <span className="card-value text-danger">
                {selectedRow?.pendingAmt || "-"}
              </span>
            </div>
          </div>

          <div className="details-section mb-4">
            <h5 className="section-title fs-16 fw-700 mb-3">Payment Details</h5>
            <div className="detail-row mb-3">
              <span className="detail-label">Customer</span>
              <div className="detail-value d-flex align-items-center gap-2">
                <div className="customer-avatar">
                  <img
                    src={selectedRow?.profile || profileIc}
                    alt=""
                    className="rounded-circle"
                    style={{ width: "24px", height: "24px" }}
                  />
                </div>
                {selectedRow?.vendor || "-"}
              </div>
            </div>
            <div className="detail-row mb-3">
              <span className="detail-label">Invoice Number</span>
              <div className="detail-value fw-600">
                {selectedRow?.invoiceNo || "-"}
              </div>
            </div>
            <div className="detail-row mb-3">
              <span className="detail-label">Payment Type</span>
              <div className="detail-value">
                <span className="badge-upi">{selectedRow?.type || "-"}</span>
              </div>
            </div>
          </div>

          {/* Bank Transaction Details Section */}
          {selectedRow?.bank_transaction_id &&
            (selectedRow?.bank_trans_validate === true ||
              selectedRow?.bank_trans_validate === 1 ||
              selectedRow?.bank_trans_validate === "true" ||
              selectedRow?.bank_trans_validate === "1") && (
              <div className="details-section mb-4 border-top pt-3">
                <h5 className="section-title fs-16 fw-700 mb-3">
                  Bank Transaction Details
                </h5>
                <div className="bank-details-card">
                  <div className="bank-card-header d-flex justify-content-center align-items-center gap-2">
                    <span className="fs-13 fw-600 textGrey">
                      Transaction ID :
                    </span>
                    <span className="fs-13 fw-700 text-purple-dark">
                      {selectedRow?.bank_transaction_id}
                    </span>
                  </div>
                  <div className="bank-card-body">
                    {/* Main Amount & Date */}
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="bank-icon-circle rounded-circle d-flex align-items-center justify-content-center">
                        <FaUniversity
                          style={{ color: "#4f46e5", fontSize: "20px" }}
                        />
                      </div>
                      <div>
                        <h4 className="drawer-amount mb-0">
                          {selectedRow?.bank_amount || "-"}
                        </h4>
                        <p className="drawer-subtext mb-0 mt-1">
                          Transaction Date :{" "}
                          <span className="text-purple-dark fw-600">
                            {selectedRow?.bank_date || "-"}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="bank-info-grid mb-3">
                      <div className="bank-info-item text-center">
                        <span className="card-label fs-11 textGrey d-block mb-1">
                          Value date
                        </span>
                        <span className="card-value fs-13 fw-600 textcolor">
                          {selectedRow?.bank_value_date || "-"}
                        </span>
                      </div>
                      <div className="bank-info-item text-center">
                        <span className="card-label fs-11 textGrey d-block mb-1">
                          Balance amount
                        </span>
                        <span className="card-value fs-13 fw-600 textcolor">
                          {selectedRow?.bank_balance || "-"}
                        </span>
                      </div>
                      <div className="bank-info-item text-center">
                        <span className="card-label fs-11 textGrey d-block mb-1">
                          Cheque No.
                        </span>
                        <span className="card-value fs-13 fw-600 textcolor">
                          {selectedRow?.bank_cheque || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="section-separator my-3"></div>

                    {/* Other Details */}
                    <div className="mb-3">
                      <h6 className="fs-14 fw-700 textcolor mb-3">
                        Other Details
                      </h6>

                      <div className="d-flex align-items-start mb-3">
                        <span
                          className="fs-13 textGrey"
                          style={{ width: "120px", flexShrink: 0 }}
                        >
                          Bank Name
                        </span>
                        <span className="fs-13 fw-600 textcolor d-flex align-items-center gap-2 text-start">
                          {selectedRow?.bank_logo ? (
                            <img
                              src={selectedRow.bank_logo}
                              alt=""
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "4px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ borderRadius: "4px", flexShrink: 0 }}
                            >
                              <rect
                                width="24"
                                height="24"
                                rx="4"
                                fill="#B01E2A"
                              />
                              <path d="M12 7H18V9H12V7Z" fill="white" />
                              <path d="M6 11H15V13H6V11Z" fill="white" />
                              <path d="M10 15H18V17H10V15Z" fill="white" />
                            </svg>
                          )}
                          {selectedRow?.bank_name || "-"}
                        </span>
                      </div>

                      <div className="d-flex align-items-start">
                        <span
                          className="fs-13 textGrey"
                          style={{ width: "120px", flexShrink: 0 }}
                        >
                          Description
                        </span>
                        <span
                          className="fs-12 textcolor text-start"
                          style={{
                            maxWidth: "235px",
                            wordBreak: "break-all",
                            lineHeight: "1.4",
                          }}
                        >
                          {selectedRow?.bank_desc || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="section-separator my-3"></div>

                    {/* Remarks */}
                    <div>
                      <h6 className="fs-14 fw-700 textcolor mb-2">Remarks</h6>
                      <p className="fs-12 text-muted-dark mb-0">
                        {selectedRow?.remarks || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {selectedRow?.attachment && (
            <div className="attachments-section mb-4 border-top pt-3">
              <h5 className="section-title fs-16 fw-700 mb-3">
                Attachments (1)
              </h5>
              <div
                className="attachment-card d-flex align-items-center gap-3 p-3"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (selectedRow?.attachment_url) {
                    setPreviewFileUrl(selectedRow.attachment_url);
                    setShowPreviewModal(true);
                  }
                }}
              >
                <div className="attachment-icon-bg">
                  <i className="bi bi-image fs-4 text-success"></i>
                </div>
                <div>
                  <div className="fs-14 fw-600">{selectedRow.attachment}</div>
                  <div className="fs-12 text-muted">Click to view</div>
                </div>
              </div>
            </div>
          )}

          <div className="drawer-footer mt-2 pt-1 border-top">
            <div className="d-flex align-items-center gap-2 text-muted fs-12">
              <CalIc style={{ width: "14px" }} />
              Created on {selectedRow?.dateTime || selectedRow?.datetime || "-"}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default PaymentHistoryListingPage;
